import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["id"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* global document */
// Helper to convert an array of objects with id to a collection keyed by id
export const arrayToCollection = items => {
  const collection = {};
  items.forEach((item, index) => {
    const id = item.id,
      rest = _objectWithoutProperties(item, _excluded);
    collection[id] = _objectSpread({
      _created: index
    }, rest);
  });
  return collection;
};

// Helper to convert a collection keyed by id to an array of objects with id
export const collectionToArray = collection => Object.entries(collection || {}).map(_ref => {
  let _ref2 = _slicedToArray(_ref, 2),
    id = _ref2[0],
    item = _ref2[1];
  return _objectSpread({
    id
  }, item);
});

// Helper to format ISO date
export const formatTimestamp = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

// Short date format for compact displays (M/D/YY)
export const formatShortDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return "".concat(d.getMonth() + 1, "/").concat(d.getDate(), "/").concat(String(d.getFullYear()).slice(-2));
};

// Calculate percentage
export const pct = (n, d) => !d ? 0 : Math.round(n / d * 100);

// Derive check status from result
export const getCheckStatus = check => {
  if (!(check !== null && check !== void 0 && check.result)) return 'unknown';
  if (check.result.passed) return 'passed';
  if (check.result.severity === 'low') return 'warning';
  return 'failed';
};

/**
 * Get the CSS class for severity color styling
 * @param {string} severity - The severity level ('high', 'medium', 'low', or undefined)
 * @returns {string} The CSS class name for the severity color
 */
export const getSeverityColorClass = severity => {
  if (severity === 'high') return 'text-danger';
  if (severity === 'medium') return 'text-warning';
  return 'text-muted';
};

// Helper to get all unique headers from rows, preserving first row's order
// and appending any new columns from subsequent rows
export const getAllHeaders = rows => {
  if (!rows || rows.length === 0) return [];
  const headersSet = new Set();
  const headers = [];
  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      if (!headersSet.has(key)) {
        headersSet.add(key);
        headers.push(key);
      }
    });
  });
  return headers;
};
const formatNumberForDisplay = v => {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v.toLocaleString('en-US');
  }
  return null;
};

// Helper to normalize cell values - converts single-element JSON arrays to plain strings
export const normalizeValue = val => {
  if (val == null) return '';
  const numStr = formatNumberForDisplay(val);
  if (numStr != null) return numStr;
  const str = String(val);
  // Try to parse as JSON if it looks like an array
  if (str.startsWith('[') && str.endsWith(']')) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        // Single element array - return just the value
        if (parsed.length === 1) {
          const one = parsed[0];
          const formatted = formatNumberForDisplay(one);
          return formatted != null ? formatted : String(one !== null && one !== void 0 ? one : '');
        }
        // Multi-element array - join with comma
        if (parsed.length > 1) {
          return parsed.map(v => {
            var _formatNumberForDispl;
            return (_formatNumberForDispl = formatNumberForDisplay(v)) !== null && _formatNumberForDispl !== void 0 ? _formatNumberForDispl : String(v !== null && v !== void 0 ? v : '');
          }).join(', ');
        }
        // Empty array
        return '';
      }
    } catch (_unused) {
      // Not valid JSON, return original
    }
  }
  return str;
};

// Helper to convert rows to CSV and trigger download
export const downloadCSV = (rows, filename) => {
  if (!rows || rows.length === 0) return;
  const headers = getAllHeaders(rows);
  const csvContent = [headers.join(','), ...rows.map(row => headers.map(h => {
    const val = normalizeValue(row[h]);
    // Escape quotes and wrap in quotes if contains comma, quote, or newline
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      return "\"".concat(val.replace(/"/g, '""'), "\"");
    }
    return val;
  }).join(','))].join('\n');
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = "".concat(filename, ".csv");
  link.click();
  URL.revokeObjectURL(url);
};

// Pattern to match existing "Results limited for only" prefix
const LIMITED_RESULTS_PREFIX_PATTERN = /^Results limited for only [^:]+: /;

/**
 * Creates a limited results prefix string for quality agent queries
 * @param {string} formTypeName - The form type name
 * @param {string} formTitle - The form title
 * @returns {string} The prefix string
 */
export const createLimitedResultsPrefix = (formTypeName, formTitle) => {
  const typeName = formTypeName || '';
  const title = formTitle || '';
  return "Results limited for only ".concat(typeName, " ").concat(title, ": ");
};

/**
 * Strips any existing limited results prefix from a query string
 * @param {string} query - The query string that may contain a prefix
 * @returns {string} The query without the prefix
 */
export const stripLimitedResultsPrefix = query => {
  if (!query || typeof query !== 'string') return query || '';
  return query.replace(LIMITED_RESULTS_PREFIX_PATTERN, '');
};

/**
 * Ensures a query has the correct limited results prefix
 * @param {string} query - The query string
 * @param {string} formTypeName - The form type name
 * @param {string} formTitle - The form title
 * @returns {string} The query with the correct prefix
 */
export const ensureLimitedResultsPrefix = (query, formTypeName, formTitle) => {
  const cleanQuery = stripLimitedResultsPrefix(query);
  const prefix = createLimitedResultsPrefix(formTypeName, formTitle);
  return prefix + cleanQuery;
};